import { useState, useEffect, useCallback, useRef } from 'react';

/** Total session timeout — 15 minutes (HIPAA guideline: 10-15 min). */
export const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

/** Warning countdown duration — 60 seconds before auto-logout. */
export const WARNING_DURATION_MS = 60 * 1000;

/** Idle threshold before showing warning — SESSION_TIMEOUT - WARNING_DURATION. */
export const IDLE_THRESHOLD_MS = SESSION_TIMEOUT_MS - WARNING_DURATION_MS;

/** Events that reset the idle timer (phase 1 only). */
export const ACTIVITY_EVENTS = [
  'mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click',
] as const;

/** Dev mode extends timeout to 60 minutes to avoid interrupting dev workflows. */
const DEV_IDLE_THRESHOLD_MS = 59 * 60 * 1000;
const DEV_WARNING_DURATION_MS = 60 * 1000;

export type SessionPhase = 'active' | 'warning' | 'expired';

export interface UseSessionTimeoutReturn {
  phase: SessionPhase;
  secondsRemaining: number;
  resetTimer: () => void;
}

/**
 * Two-phase session timeout hook.
 *
 * Phase 1 (active): Resets on any user activity event. After IDLE_THRESHOLD_MS
 * of inactivity, transitions to "warning".
 *
 * Phase 2 (warning): 60-second countdown. Activity events do NOT reset this —
 * only an explicit resetTimer() call (from the "Stay Logged In" button) returns
 * to "active". This prevents accidental bumps from hiding the security warning.
 *
 * Phase 3 (expired): Countdown hit zero. Consumer should trigger logout.
 */
export function useSessionTimeout(enabled: boolean): UseSessionTimeoutReturn {
  const [phase, setPhase] = useState<SessionPhase>('active');
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.floor(WARNING_DURATION_MS / 1000)
  );

  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const isDev = import.meta.env.DEV;
  const idleThreshold = isDev ? DEV_IDLE_THRESHOLD_MS : IDLE_THRESHOLD_MS;
  const warningSeconds = Math.floor(
    (isDev ? DEV_WARNING_DURATION_MS : WARNING_DURATION_MS) / 1000
  );

  // Start the idle timer (phase 1)
  const startIdleTimer = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    clearInterval(countdownRef.current);
    setPhase('active');
    setSecondsRemaining(warningSeconds);

    idleTimerRef.current = setTimeout(() => {
      // Transition to warning phase
      setPhase('warning');
      setSecondsRemaining(warningSeconds);

      let remaining = warningSeconds;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setSecondsRemaining(remaining);
        if (remaining <= 0) {
          clearInterval(countdownRef.current);
          setPhase('expired');
        }
      }, 1000);
    }, idleThreshold);
  }, [idleThreshold, warningSeconds]);

  // Reset timer — called by "Stay Logged In" button
  const resetTimer = useCallback(() => {
    startIdleTimer();
  }, [startIdleTimer]);

  // Set up activity listeners (only reset during "active" phase)
  useEffect(() => {
    if (!enabled) return;

    startIdleTimer();

    const handleActivity = () => {
      // Only reset during active phase — warning phase is sticky
      setPhase((current) => {
        if (current === 'active') {
          // Restart idle timer
          clearTimeout(idleTimerRef.current);
          idleTimerRef.current = setTimeout(() => {
            setPhase('warning');
            setSecondsRemaining(warningSeconds);

            let remaining = warningSeconds;
            countdownRef.current = setInterval(() => {
              remaining -= 1;
              setSecondsRemaining(remaining);
              if (remaining <= 0) {
                clearInterval(countdownRef.current);
                setPhase('expired');
              }
            }, 1000);
          }, idleThreshold);
        }
        return current;
      });
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      clearTimeout(idleTimerRef.current);
      clearInterval(countdownRef.current);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [enabled, startIdleTimer, idleThreshold, warningSeconds]);

  return { phase, secondsRemaining, resetTimer };
}
